import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaTruck, FaHeadset, FaRoute } from 'react-icons/fa';
import { MdNotifications } from 'react-icons/md';
import { toast } from 'react-toastify';
import API from '../api/axios';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    activeDrivers: 0,
    jobsInProgress: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivities();
  }, []);

  const refreshDashboard = async () => {
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchRecentActivities()
      ]);
      toast.success('Dashboard refreshed successfully');
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      toast.error('Failed to refresh dashboard');
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await API.get('/admin/dashboard/stats');
      if (response.data.success) {
        setStats({
          activeDrivers: response.data.data.activeDrivers || 0,
          jobsInProgress: response.data.data.jobsInProgress || 0
        });
      } else {
        console.error('Failed to fetch dashboard stats');
        toast.error('Failed to load dashboard statistics');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      const response = await API.get('/admin/dashboard/activities', {
        params: { limit: 3 }
      });
      if (response.data.success) {
        setRecentActivities(response.data.data || []);
      } else {
        console.error('Failed to fetch recent activities');
        toast.error('Failed to load recent activities');
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      toast.error('Failed to load recent activities');
      setRecentActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Manage Drivers',
      description: 'View and manage driver accounts',
      icon: <FaUsers className="text-blue-500" />,
      link: '/admin/drivers',
      color: 'bg-blue-50 hover:bg-blue-100'
    },
    {
      title: 'Job Management',
      description: 'Monitor and assign delivery jobs',
      icon: <FaTruck className="text-green-500" />,
      link: '/admin/jobs',
      color: 'bg-green-50 hover:bg-green-100'
    },
    {
      title: 'Predefined Jobs',
      description: 'Create and manage job templates',
      icon: <FaRoute className="text-purple-500" />,
      link: '/admin/predefined-jobs',
      color: 'bg-purple-50 hover:bg-purple-100'
    },
    {
      title: 'Support',
      description: 'Handle customer support tickets',
      icon: <FaHeadset className="text-indigo-500" />,
      link: '/admin/support',
      color: 'bg-indigo-50 hover:bg-indigo-100'
    }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-10 bg-gray-200 rounded w-64 mb-2 animate-pulse"></div>
              <div className="h-6 bg-gray-200 rounded w-80 animate-pulse"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
        </div>

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-gray-200 rounded-lg w-12 h-12 animate-pulse"></div>
                <div className="ml-4 flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions Skeleton */}
        <div className="mb-8">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 p-6 rounded-lg border animate-pulse">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gray-200 rounded mr-3"></div>
                  <div className="h-6 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-48"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity Skeleton */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4 animate-pulse"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded animate-pulse">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 rounded mr-3"></div>
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-5 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Welcome to the Adira Logistics admin panel</p>
          </div>
          <button
            onClick={refreshDashboard}
            disabled={loading || activitiesLoading}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Drivers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeDrivers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FaTruck className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Jobs in Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.jobsInProgress}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className={`${action.color} p-6 rounded-lg border transition-colors duration-200`}
            >
              <div className="flex items-center mb-3">
                <div className="text-2xl mr-3">{action.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900">{action.title}</h3>
              </div>
              <p className="text-gray-600 text-sm">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {activitiesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              <span className="ml-3 text-gray-600">Loading activities...</span>
            </div>
          ) : recentActivities.length === 0 ? (
            <p className="text-center text-gray-500">No recent activities to show.</p>
          ) : (
            recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3">
                    {activity.type === 'job' && (
                      <FaTruck className="text-blue-600 text-lg" />
                    )}
                    {activity.type === 'user' && (
                      <FaUsers className="text-green-600 text-lg" />
                    )}
                    {activity.type === 'notification' && (
                      <MdNotifications className="text-purple-600 text-lg" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500">{activity.timeAgo}</span>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      activity.status === 'AVAILABLE' || activity.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : activity.status === 'completed' 
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer with Privacy Policy Links */}
      <div className="mt-12 bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Adira Logistics System &copy; {new Date().getFullYear()}
          </p>
          <div className="flex justify-center space-x-6">
            <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 text-sm underline">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800 text-sm underline">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 